// src/features/usage/api/quotaApi.ts
import { bff } from '@/lib/http/bff';

// ── Types ────────────────────────────────────────────────────────────────

export interface QuotaRow {
  tenantId:         number;
  creditBalance:    number;
  lifetimeCredits:  number;
  softLimitDaily:   number;
  hardLimitDaily:   number;
  creditsPerUsd:    number;
  overageRate:      number;
  isMeteringActive: boolean;
  createdAt:        string;
  updatedAt:        string;
}

export interface QuotaListResponse {
  items: QuotaRow[];
}

export interface AtRiskRow {
  tenantId:          number;
  creditBalance:     number;
  hardLimitDaily:    number;
  todayRequests:     number;
  todayCostUsd:      number;
  todayPctHardLimit: number;
  isMeteringActive:  boolean;
}

export interface AtRiskResponse {
  threshold: number;
  items:     AtRiskRow[];
}

export interface CreditGrantPayload {
  credits:      number;
  description?: string;
}

export interface CreditGrantResult {
  ledgerId:   number;
  entryType:  string;
  credits:    number;
  newBalance: number;
  createdAt:  string;
}

export interface QuotaCreatePayload {
  tenant_id:        number;
  credit_balance?:  number;
  soft_limit_daily?: number;
  hard_limit_daily?: number;
  credits_per_usd?: number;
  overage_rate?:    number;
}

export interface QuotaUpdatePayload {
  soft_limit_daily?: number;
  hard_limit_daily?: number;
  credits_per_usd?:  number;
  overage_rate?:     number;
}

export interface SuspendPayload {
  is_metering_active: boolean;
}

// ── API calls ────────────────────────────────────────────────────────────

const BASE = '/quotas';

export const fetchQuotaList = (): Promise<QuotaListResponse> =>
  bff.get<QuotaListResponse>(BASE);

export const fetchQuota = (tenantId: number): Promise<QuotaRow> =>
  bff.get<QuotaRow>(`${BASE}/${tenantId}`);

export const fetchAtRisk = (threshold?: number): Promise<AtRiskResponse> => {
  const qs = threshold != null ? `?threshold=${threshold}` : '';
  return bff.get<AtRiskResponse>(`${BASE}/at-risk${qs}`);
};

export const createQuota = (payload: QuotaCreatePayload): Promise<QuotaRow> =>
  bff.post<QuotaRow, QuotaCreatePayload>(BASE, payload);

export const updateQuota = (tenantId: number, payload: QuotaUpdatePayload): Promise<QuotaRow> =>
  bff.put<QuotaRow, QuotaUpdatePayload>(`${BASE}/${tenantId}`, payload);

export const grantCredits = (tenantId: number, payload: CreditGrantPayload): Promise<CreditGrantResult> =>
  bff.post<CreditGrantResult, CreditGrantPayload>(`${BASE}/${tenantId}/credits`, payload);

export const suspendQuota = (tenantId: number, payload: SuspendPayload): Promise<QuotaRow> =>
  bff.post<QuotaRow, SuspendPayload>(`${BASE}/${tenantId}/suspend`, payload);
